import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { signOut } from '../utils/auth';
import { ROLE_LABELS } from '../constants/accessControl';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import GlobalSearch from './GlobalSearch';
import MessagesModal from './modals/MessagesModal';
import NotificationsModal from './modals/NotificationsModal';
import { fetchNotifications, subscribeToNotifications, getUnreadNotificationsCount, markNotificationAsRead } from '../utils/notificationService';
import { fetchContacts, getUnreadMessageCount, markAllMessagesFromSenderAsRead, subscribeToMessages } from '../utils/messageService';

const Navbar = ({ toggleMobileMenu, isMobileMenuOpen }) => {
  const { user, role } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [showMessagesDropdown, setShowMessagesDropdown] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  
  const [notifications, setNotifications] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);
  const [notificationError, setNotificationError] = useState(null);
  const [messageError, setMessageError] = useState(null);
  
  // Get counts from notifications and messages data
  const unreadNotificationsCount = getUnreadNotificationsCount(notifications);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  
  // Recent notifications and contacts (just first 3 of each)
  const recentNotifications = Array.isArray(notifications) ? notifications.slice(0, 3) : [];
  const recentContacts = Array.isArray(contacts) ? contacts.slice(0, 3) : [];
  
  // New High / Critical alerts (and alerts assigned to me) pop up briefly
  const [alertToast, setAlertToast] = useState(null);
  const alertToastTimer = useRef(null);
  useEffect(() => () => clearTimeout(alertToastTimer.current), []);

  // Fetch notifications on mount, then keep them current (realtime + 30 s polling)
  useEffect(() => {
    let live = true;
    const loadNotifications = async () => {
      try {
        const notificationsData = await fetchNotifications();
        if (!live) return;
        setNotifications(Array.isArray(notificationsData) ? notificationsData : []);
        setNotificationError(null);
      } catch (err) {
        console.error('Error loading notifications:', err);
        if (live) setNotificationError('Failed to load notifications');
      } finally {
        if (live) setIsLoadingNotifications(false);
      }
    };

    setIsLoadingNotifications(true);
    loadNotifications();

    // Events carry { eventType, new, old }; the polling fallback sends the whole list as 'SYNC'
    const subscription = subscribeToNotifications((event) => {
      if (event?.eventType === 'SYNC' && Array.isArray(event.new)) {
        setNotifications(event.new);
        setNotificationError(null); // a poll that worked clears an earlier failed load
        return;
      }
      const n = event?.new;
      if (event?.eventType === 'INSERT' && n && (!n.user_id || n.user_id === user?.id) && /^alert_(critical|high|assigned)$/.test(n.type || '')) {
        clearTimeout(alertToastTimer.current);
        setAlertToast({ title: n.title || 'New alert', message: n.message, critical: n.type === 'alert_critical' });
        alertToastTimer.current = setTimeout(() => setAlertToast(null), 8000);
      }
      loadNotifications();
    }, { userId: user?.id });

    return () => {
      live = false;
      subscription.unsubscribe();
    };
  }, [user?.id]);
  
  // Fetch messages and contacts on component mount
  useEffect(() => {
    const loadContacts = async () => {
      try {
        setIsLoadingMessages(true);
        const contactsData = await fetchContacts();
        setContacts(contactsData);
        
        // Get the unread message count
        const count = await getUnreadMessageCount();
        setUnreadMessagesCount(count);
        setMessageError(null);
      } catch (err) {
        console.error('Error loading messages:', err);
        // Set default values for fallback state
        setContacts([]);
        setUnreadMessagesCount(0);
        setMessageError(null); // Don't show error in UI for database setup issues
      } finally {
        setIsLoadingMessages(false);
      }
    };
    
    loadContacts();
    
    // Subscribe to real-time message updates
    const subscription = subscribeToMessages(async () => {
      // When messages change, update contacts and count
      try {
        const contactsData = await fetchContacts();
        setContacts(contactsData);
        const count = await getUnreadMessageCount();
        setUnreadMessagesCount(count);
      } catch (err) {
        console.error('Error in message subscription:', err);
      }
    });
    
    return () => {
      // Clean up subscription when component unmounts
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  }, []);

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/login';
  };

  const toggleProfileMenu = () => {
    setShowProfileMenu(!showProfileMenu);
    if (showNotificationsDropdown) setShowNotificationsDropdown(false);
    if (showMessagesDropdown) setShowMessagesDropdown(false);
  };

  const toggleNotificationsDropdown = () => {
    setShowNotificationsDropdown(!showNotificationsDropdown);
    if (showProfileMenu) setShowProfileMenu(false);
    if (showMessagesDropdown) setShowMessagesDropdown(false);
  };
  
  const toggleMessagesDropdown = () => {
    setShowMessagesDropdown(!showMessagesDropdown);
    if (showProfileMenu) setShowProfileMenu(false);
    if (showNotificationsDropdown) setShowNotificationsDropdown(false);
  };
  
  const openNotificationsModal = () => {
    setShowNotificationsModal(true);
    setShowNotificationsDropdown(false);
  };
  
  const openMessagesModal = () => {
    setShowMessagesModal(true);
    setShowMessagesDropdown(false);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 md:left-64 right-0 h-14 z-10" style={{ background: 'var(--td-navbar-bg)', borderBottom: '1px solid var(--td-navbar-border)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <div className="h-full px-4 flex justify-between items-center">
          {/* Mobile hamburger menu */}
          <button
            className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            style={{ color: 'var(--td-navbar-icon)' }}
            onClick={toggleMobileMenu}
          >
            <span className="sr-only">Open sidebar</span>
            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          {/* Brand logo and name (visible on mobile when sidebar is hidden) */}
          <div className="flex items-center md:hidden">
            <Link to="/dashboard" className="flex items-center">
              <img src="/logo.svg" alt="TrashDrop Logo" className="h-8 w-auto" />
              {/* The logo already reads "TrashDrop"; the name is repeated only where the bar has room */}
              <span className="ml-2 text-lg font-semibold hidden sm:inline" style={{ color: 'var(--td-accent)' }}>TrashDrop</span>
            </Link>
          </div>
          
          {/* Search: pick requests or dumping reports, filtered by the term */}
          <GlobalSearch role={role} />
          
          {/* Right side icons */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Light or dark dashboard; remembered in this browser */}
            <button
              type="button"
              onClick={toggleTheme}
              className="p-2 hover:text-green-600"
              style={{ color: 'var(--td-navbar-icon)' }}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Light mode' : 'Dark mode'}
              data-testid="theme-toggle"
            >
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`} aria-hidden="true"></i>
            </button>
            {/* Messages icon */}
            <div className="relative">
              <button
                onClick={toggleMessagesDropdown}
                className="p-2 hover:text-green-600 focus:outline-none"
                style={{ color: 'var(--td-navbar-icon)' }}
                aria-label="Messages"
              >
                <i className="fas fa-comment-alt"></i>
                {unreadMessagesCount > 0 && (
                  <span className="absolute top-0 right-0 bg-red-700 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {unreadMessagesCount}
                  </span>
                )}
              </button>
              
              {/* Messages dropdown */}
              {showMessagesDropdown && (
                <div className="absolute right-0 mt-2 w-80 rounded-lg shadow-lg overflow-hidden z-20" style={{ background: 'var(--td-dropdown-bg)', border: '1px solid var(--td-dropdown-border)' }}>
                  <div className="px-4 py-3 border-b flex justify-between items-center" style={{ borderColor: 'var(--td-dropdown-border)', background: 'var(--td-dropdown-hover)' }}>
                    <h3 className="font-semibold" style={{ color: 'var(--td-text-primary)' }}>Messages</h3>
                    <button 
                      onClick={openMessagesModal}
                      className="text-sm font-medium text-green-600 hover:text-green-700"
                    >
                      View all
                    </button>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {/* Preview of recent messages would go here */}
                    <div className="p-4 text-center">
                      <button
                        onClick={openMessagesModal}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Open Messages
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Notification icon */}
            <div className="relative">
              <button
                onClick={toggleNotificationsDropdown}
                className="p-2 hover:text-green-600 focus:outline-none"
                style={{ color: 'var(--td-navbar-icon)' }}
                aria-label="Notifications"
              >
                <i className="fas fa-bell"></i>
                {unreadNotificationsCount > 0 && (
                  <span className="absolute top-0 right-0 bg-red-700 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {unreadNotificationsCount}
                  </span>
                )}
              </button>
              
              {/* Notification dropdown */}
              {showNotificationsDropdown && (
                <div className="absolute right-0 mt-2 w-80 rounded-lg shadow-lg overflow-hidden z-20" style={{ background: 'var(--td-dropdown-bg)', border: '1px solid var(--td-dropdown-border)' }}>
                  <div className="py-2 px-4 border-b" style={{ borderColor: 'var(--td-dropdown-border)' }}>
                    <h3 className="font-semibold" style={{ color: 'var(--td-text-primary)' }}>Notifications</h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {isLoadingNotifications ? (
                      <div className="p-4 text-center">
                        <p className="text-td-sage">Loading notifications...</p>
                      </div>
                    ) : notificationError ? (
                      <div className="p-4 text-center">
                        <p className="text-red-700">{notificationError}</p>
                      </div>
                    ) : recentNotifications.length === 0 ? (
                      <div className="p-4 text-center">
                        <p className="text-td-sage">No notifications</p>
                      </div>
                    ) : recentNotifications.map(notification => (
                      <div 
                        key={notification.id} 
                        className="px-4 py-3 border-b cursor-pointer"
                        style={{ borderColor: 'var(--td-dropdown-border)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--td-dropdown-hover)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        onClick={() => markNotificationAsRead(notification.id).then(ok => {
                          // Only show it as read once the database has saved it
                          if (ok) setNotifications(prev => prev.map(n =>
                            n.id === notification.id ? {...n, read: true} : n
                          ));
                        })}
                      >
                        <div className="flex items-start">
                          <div className={`mt-1 mr-3 rounded-full p-2 ${
                            notification.type === 'alert' ? 'bg-red-100 text-red-700' : 
                            notification.type === 'info' ? 'bg-blue-100 text-td-forest' : 
                            'bg-td-tonal text-td-forest'
                          }`}>
                            <i className={`fas ${
                              notification.type === 'alert' ? 'fa-exclamation-triangle' : 
                              notification.type === 'info' ? 'fa-info-circle' : 
                              'fa-check-circle'
                            } text-sm`}></i>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium" style={{ color: 'var(--td-text-primary)' }}>{notification.message}</p>
                            <p className="text-xs" style={{ color: 'var(--td-text-muted)' }}>{notification.time}</p>
                          </div>
                          {!notification.read && (
                            <span className="h-2 w-2 bg-green-600 rounded-full mt-2"></span>
                          )}
                        </div>
                      </div>
                    ))}
                    <div className="p-3 text-center border-t">
                      <button
                        onClick={openNotificationsModal}
                        className="text-green-600 hover:text-green-700 font-medium text-sm"
                      >
                        View all notifications
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* User profile */}
            <div className="relative">
              <button
                onClick={toggleProfileMenu}
                className="flex items-center space-x-2 focus:outline-none"
              >
                <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center">
                  {user?.user_metadata?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                </div>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-semibold">{user?.user_metadata?.full_name || 'User'}</div>
                  <div className="text-xs text-td-sage">{ROLE_LABELS[role] || 'User'}</div>
                </div>
                <i className="fas fa-chevron-down text-xs text-td-sage"></i>
              </button>
              
              {/* Profile dropdown */}
              {showProfileMenu && (
                <div className="absolute right-0 mt-2 w-48 rounded-lg shadow-lg overflow-hidden z-20" style={{ background: 'var(--td-dropdown-bg)', border: '1px solid var(--td-dropdown-border)' }}>
                  <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--td-dropdown-border)' }}>
                    <p className="text-sm font-semibold" style={{ color: 'var(--td-text-primary)' }}>{user?.user_metadata?.full_name || 'User'}</p>
                    <p className="text-xs" style={{ color: 'var(--td-text-muted)' }}>{user?.email}</p>
                  </div>
                  <div>
                    <Link to="/settings" className="block px-4 py-2 transition-colors" style={{ color: 'var(--td-text-primary)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--td-dropdown-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <i className="fas fa-cog mr-2"></i> Settings
                    </Link>
                    <button 
                      onClick={handleSignOut} 
                      className="w-full text-left px-4 py-2 text-red-700 transition-colors"
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--td-dropdown-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <i className="fas fa-sign-out-alt mr-2"></i> Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
      
      {alertToast && (
        <div role="alert" className={`fixed top-16 right-4 z-[4500] max-w-sm rounded-lg shadow-xl px-4 py-3 text-sm text-white ${alertToast.critical ? 'bg-red-700' : 'bg-orange-600'}`} data-testid="alert-toast">
          <div className="flex items-start gap-2">
            <i className="fas fa-exclamation-triangle mt-0.5" aria-hidden="true"></i>
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{alertToast.title}</p>
              {alertToast.message && <p className="truncate">{alertToast.message}</p>}
              <Link to="/request-pickup/alerts" className="underline text-xs" onClick={() => setAlertToast(null)}>Open alerts</Link>
            </div>
            <button type="button" aria-label="Dismiss" onClick={() => setAlertToast(null)} className="leading-none text-lg">&times;</button>
          </div>
        </div>
      )}

      {/* Modals */}
      <MessagesModal isOpen={showMessagesModal} onClose={() => setShowMessagesModal(false)} />
      <NotificationsModal
        isOpen={showNotificationsModal}
        onClose={() => setShowNotificationsModal(false)}
        onRead={(id) => setNotifications(prev => prev.map(n => (id === null || n.id === id ? { ...n, read: true } : n)))}
      />
    </>
  );
};

export default Navbar;
